import { forwardRef, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export const Sheet = Dialog.Root
export const SheetTrigger = Dialog.Trigger

export interface SheetContentProps {
  children: ReactNode
  className?: string
}

export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(
  ({ children, className }, ref) => (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <Dialog.Content
        ref={ref}
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-full sm:w-[360px]',
          'bg-white shadow-xl flex flex-col',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'duration-200 ease-out',
          className,
        )}
      >
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  ),
)
SheetContent.displayName = 'SheetContent'

export interface SheetHeaderProps {
  children: ReactNode
  className?: string
}

export function SheetHeader({ children, className }: SheetHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-1 border-b border-gray-200 px-4 py-4', className)}>
      {children}
    </div>
  )
}

export interface SheetTitleProps {
  children: ReactNode
  className?: string
}

export const SheetTitle = forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ children, className }, ref) => (
    <Dialog.Title ref={ref} className={cn('text-body font-semibold text-gray-900', className)}>
      {children}
    </Dialog.Title>
  ),
)
SheetTitle.displayName = 'SheetTitle'

export interface SheetBodyProps {
  children: ReactNode
  className?: string
}

export function SheetBody({ children, className }: SheetBodyProps) {
  return (
    <div className={cn('flex-1 overflow-y-auto px-4 py-4', className)}>
      {children}
    </div>
  )
}

export function SheetClose({ className }: { className?: string }) {
  return (
    <Dialog.Close
      className={cn(
        'absolute right-3 top-3 rounded p-1 text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
        className,
      )}
      aria-label="Schließen"
    >
      <X className="h-4 w-4" />
    </Dialog.Close>
  )
}
